<template>
  <div
    class="travel-date-picker"
    v-click-outside="closeCalendar"
  >
    <button
      class="selected-date"
      @click="toggleCalendar"
    >
      <span class="travel-date-picker__display-date">
        <svg class="travel-date-picker__calendar-icon">
          <use :xlink:href="`#${calendar}`" />
        </svg>
        <div class="travel-date-picker__display-text">
          <span class="selected-date__label">
            {{ $t('search.departureDate') }}
          </span>
          {{ formattedDate }}
        </div>
      </span>
    </button>
    <div
      v-if="showCalendar"
      class="travel-date-picker__calendar-overlay"
    >
      <v-date-picker
        ref="datepicker"
        v-model="selectedDate"
        @update:model-value="handleDateSelect"
        :columns="1"
        :locale="getCurrentLocale()"
        class="vc-custom"
        :min-date="minDate"
        :is-required="true"
        :initial-page="initialPage"
        :masks="{
          title: 'MMMM YYYY',
          weekdays: 'WWW',
          input: ['L', 'DD.MM.YYYY', 'DD.MM.YY'],
          dayPopover: 'WWW, MMM D, YYYY',
        }"
      >
        <template #nav-left-button="{ move }">
          <button
            type="button"
            class="vc-arrow-left"
            @click="() => move()"
          >
            <i class="vc-arrow-left" />
          </button>
        </template>
        <template #nav-right-button="{ move }">
          <button
            type="button"
            class="vc-arrow vc-next"
            @click="() => move()"
          >
            <i class="vc-arrow-right" />
          </button>
        </template>
      </v-date-picker>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, onMounted, watch } from 'vue'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { format, addHours } from 'date-fns'
  import { nb, enUS } from 'date-fns/locale'
  import calendar from '@/assets/icons/calendar.svg'
  import { getCurrentLocale } from '@/i18n'

  export default defineComponent({
    name: 'TravelDate',
    components: {
      calendar,
    },
    props: {
      initialDate: {
        type: String,
        default: null,
      },
    },
    directives: {
      clickOutside: {
        beforeMount(el, binding) {
          el.clickOutsideEvent = (event: MouseEvent) => {
            if (!(el === event.target || el.contains(event.target))) {
              binding.value(event, el)
            }
          }
          document.body.addEventListener('click', el.clickOutsideEvent)
        },
        unmounted(el) {
          document.body.removeEventListener('click', el.clickOutsideEvent)
        },
      },
    },
    setup(props) {
      const journeyStore = useJourneyStore()
      const showCalendar = ref(false)
      const selectedDate = ref<Date | null>(null)
      const datepicker = ref<any>(null)

      const minDate = computed(() => new Date())

      const initialPage = computed(() => {
        let dateToUse: Date

        if (journeyStore.departureDate) {
          dateToUse = new Date(journeyStore.departureDate)
        } else {
          dateToUse = new Date()
        }

        return {
          month: dateToUse.getMonth() + 1,
          year: dateToUse.getFullYear(),
        }
      })

      const formattedDate = computed(() => {
        const currentLocale = getCurrentLocale()
        const locale = currentLocale === 'no' ? nb : enUS

        if (selectedDate.value) {
          return format(selectedDate.value, 'dd. MMM', { locale }).toLowerCase()
        }
        return format(minDate.value, 'dd. MMM', { locale }).toLowerCase()
      })

      const toggleCalendar = () => {
        showCalendar.value = !showCalendar.value
      }

      const closeCalendar = () => {
        showCalendar.value = false
      }

      const handleDateSelect = (date: Date) => {
        console.log('[TRAVEL-DATE] Date selected by user:', date)
        
        const year = date.getFullYear()
        const month = date.getMonth()
        const day = date.getDate()
        
        const stableDate = new Date(year, month, day, 12, 0, 0, 0)        
        selectedDate.value = stableDate
        showCalendar.value = false
        
        const formattedForStore = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        console.log('[TRAVEL-DATE] Setting departure date in journeyStore:', formattedForStore)
        
        journeyStore.setDepartureDate(formattedForStore)
        
        // Verify the date was set correctly
        console.log('[TRAVEL-DATE] Journey store departure date after setting:', journeyStore.departureDate)
        
        if (journeyStore.returnDate) {
          console.log('[TRAVEL-DATE] Checking return date compatibility with new departure:', journeyStore.returnDate)
          try {
            const returnParts = journeyStore.returnDate.split('-').map(part => parseInt(part, 10))
            if (returnParts.length === 3) {
              const [returnYear, returnMonth, returnDay] = returnParts
              const returnDate = new Date(returnYear, returnMonth - 1, returnDay, 12, 0, 0, 0)
              
              if (returnDate < stableDate) {
                console.log('[TRAVEL-DATE] Return date is before departure, clearing return date')
                journeyStore.setReturnDate(null)
              } else {
                console.log('[TRAVEL-DATE] Return date is valid, keeping:', journeyStore.returnDate)
              }
            }
          } catch (error) {
            console.error('Error comparing dates:', error)
          }
        } else {
          console.log('[TRAVEL-DATE] No return date set')
        }
      }

      onMounted(() => {        
        if (props.initialDate) {
          const parts = props.initialDate.split('-').map(part => parseInt(part, 10))
          
          if (parts.length === 3) {
            const [year, month, day] = parts
            const stableDate = new Date(year, month - 1, day, 12, 0, 0, 0)
            
            selectedDate.value = stableDate
            journeyStore.setDepartureDate(props.initialDate)
          }
        } else if (journeyStore.departureDate) {
          const parts = journeyStore.departureDate.split('-').map(part => parseInt(part, 10))
            
          if (parts.length === 3) {
            const [year, month, day] = parts
            const stableDate = new Date(year, month - 1, day, 12, 0, 0, 0)
            
            selectedDate.value = stableDate
          }
        } else {
          const now = new Date()
          const year = now.getFullYear()
          const month = now.getMonth()
          const day = now.getDate()
          
          const stableDate = new Date(year, month, day, 12, 0, 0, 0)
          
          selectedDate.value = stableDate
          const formattedDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
          journeyStore.setDepartureDate(formattedDate)
        }
      })

      return {
        showCalendar,
        selectedDate,
        datepicker,
        calendar,
        minDate,
        initialPage,
        formattedDate,
        toggleCalendar,
        closeCalendar,
        handleDateSelect,
        getCurrentLocale,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .travel-date-picker {
    display: flex;
    flex-direction: column;
    position: relative;

    .selected-date {
      @include body-large;
      display: flex;
      align-items: center;
      gap: 16px;
      min-width: 120px;
      padding: 1px;
      text-align: left;
      color: $clr-text;
      background-color: $clr-background;
      border: 1px solid $clr-primary-50;
      border-radius: $border-radius;
      box-shadow: $box-shadow-card;

      @media screen and (min-width: $breakpoint-sm) {
        min-width: 165px;
      }

      .modal-overlay & {
        min-width: 149px;
      }

      &__label {
        position: absolute;
        top: rem(10);
        left: 40px;
        color: $clr-heading;
      }
    }

    &__calendar-icon {
      position: relative;
      top: -10px;
      left: 0;
      width: 15px;
      height: 15px;
      color: $clr-secondary;
      margin-right: 8px;
    }

    &__toggle-button {
      @include body-large;
      background: transparent;
      border: none;
      border-radius: $border-radius;
      display: flex;
      flex-direction: row;

      svg {
        position: relative;
        top: -10 px;
        left: 0;
        width: 1 5px;
        height: 15px;
        color: $clr-secondary;
        margin-right: 8px;
      }
    }
    &__display-date {
      padding: rem(28) 0 rem(10) 15px;
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    &__display-text {
      display: flex;
      flex-direction: column;
      text-align: left;
      span {
        @include body-small;
      }
    }

    &__calendar-overlay {
      position: absolute;
      top: 100%;
      z-index: 1000;
      left: 95%;
      transform: translateX(-50%);
      background-color: white;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

      @media (max-width: $breakpoint-xs) {
        right: initial;
        top: 0;
        left: 85%;
      }
    }

    :deep(.vc-custom) {
      .vc-highlight {
        background-color: #2a347a !important;
        border-radius: 0 !important;
        width: 32px !important;
        height: 32px !important;

        &.vc-day-content {
          color: white !important;
          background-color: #2a347a !important;
          border-radius: 0 !important;
        }
      }

      // Header styles
      .vc-header {
        color: #2a347a;

        .vc-title {
          color: #2a347a;
          background: transparent !important;

          &:hover {
            background: transparent !important;
          }
        }

        .vc-arrow {
          background: transparent !important;
        }

        button {
          background: transparent !important;
          &:hover {
            background: transparent !important;
          }
        }
      }

      // Calendar title button (month/year)
      .vc-title-wrapper {
        button {
          background: transparent !important;
          &:hover {
            background: transparent !important;
          }
        }
      }

      // Navigation buttons
      .vc-arrow-left,
      .vc-arrow-right,
      button.vc-nav-arrow {
        width: 26px !important;
        height: 26px !important;
        background: transparent !important;
        border: none !important;
        padding: 4px !important;
        font-size: 20px !important;
        color: #2a347a !important;
        cursor: pointer !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;

        &:hover {
          background: transparent !important;
        }
      }

      // Month/year popover styles
      .vc-popover-content {
        * {
          background: white !important;
          &:hover {
            background: #f4f5f8 !important;
          }
        }

        .vc-nav-title {
          background: transparent !important;
        }

        button {
          background: transparent !important;
          &:hover {
            background: #f4f5f8 !important;
          }
        }
      }

      // Force transparent background on all navigation elements
      [class*='vc-'] {
        &[class*='-arrow'],
        &[class*='-button'],
        &[class*='-nav'],
        &[class*='-title'] {
          background: transparent !important;
          &:hover {
            background: transparent !important;
          }
        }
      }
    }
  }
</style>
